import { Box, useTheme } from '@mui/material';
import { Category, CategoryKey, SeatingChart } from '@seatsio/seatsio-react';
import { RefObject, useEffect, useRef, useState } from 'react';

import { useModalControl } from 'hooks/useModalControl';

import BorderStack from 'components/@common/BorderStack';
import {
  MinimalIconButton,
  ShadowLeft,
  ShadowRight,
} from 'components/@common/HorizontalScroll/HorizontalScroll.styles';
import Icon from 'components/@icons/Icon';
import CategoryFilterDialog from 'components/@seating/CategoryFilterDialog';

import CategoryItem from './CategoryItem';

interface Props {
  subtitle: string | null;
  categories: Array<Category>;
  initialFilteredCategory: Array<CategoryKey>;
  chartRef: RefObject<SeatingChart | null>;
}

const CategorySelector = ({
  subtitle,
  categories,
  initialFilteredCategory,
  chartRef,
}: Props) => {
  const { direction } = useTheme();
  const labelRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const boxRef = useRef<HTMLDivElement>(null);
  const { open, onOpen, onClose } = useModalControl();

  const [filteredCategories, setFilteredCategories] = useState<
    Array<CategoryKey>
  >(initialFilteredCategory);

  const handleFilterCategory = (categoryKey: CategoryKey) => {
    setFilteredCategories((prev) => {
      if (prev.includes(categoryKey)) {
        return prev.filter((key) => key !== categoryKey);
      } else {
        return [...prev, categoryKey];
      }
    });
  };

  const handleNext = () => {
    const scrollByLeft = direction === 'ltr' ? 150 : -150;
    boxRef.current?.scrollBy({
      left: scrollByLeft,
      behavior: 'smooth',
    });
  };

  const handlePrev = () => {
    const scrollByLeft = direction === 'ltr' ? -150 : 150;
    boxRef.current?.scrollBy({
      left: scrollByLeft,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.changeConfig({ filteredCategories });
      chartRef.current.zoomToFilteredCategories();
    }
  }, [chartRef, filteredCategories]);

  // Scrolls to the first category when opening the view
  useEffect(() => {
    if (initialFilteredCategory && categories.length > 0) {
      labelRefs.current[initialFilteredCategory[0]]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'end',
        block: 'end',
      });
    }
  }, [initialFilteredCategory, categories.length]);

  return (
    <>
      <BorderStack
        sx={(theme) => ({
          overflow: 'hidden',
          flexDirection: 'row',
          position: 'relative',
          border: { xs: 'none', md: `1px solid ${theme.vars.palette.divider}` },
          px: { xs: 0, md: 1.5 },
          py: { xs: 1, md: 2 },
          gridArea: 'filter',
        })}
      >
        <ShadowLeft />
        <ShadowRight sx={{ right: { xs: 40, md: 44 } }} />

        <Box
          ref={boxRef}
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: 'row',
            position: 'relative',
            pl: { xs: 2, md: 4.5 },
            pr: { xs: 10, md: 10 },
            minHeight: 32,
            overflowX: 'scroll',
            overflowY: 'hidden',
            scrollBehavior: 'smooth',

            // Added to remove scrollbar on FireFox
            scrollbarWidth: 'none',

            WebkitOverflowScrolling: 'touch',

            '::-webkit-scrollbar': {
              display: 'none',
            },

            scrollSnapType: 'x mandatory',
          }}
        >
          {categories.map((category) => (
            <CategoryItem
              key={category.key}
              category={category}
              selected={filteredCategories.includes(category.key)}
              onClick={handleFilterCategory}
              ref={(el) => {
                labelRefs.current[category.key] = el;
              }}
            />
          ))}
        </Box>

        <MinimalIconButton
          sx={{
            top: 16,
            left: 8,
            display: { xs: 'none', md: 'block' },
          }}
          onClick={handlePrev}
        >
          <Icon name="ChevronLeft" color="text.secondary" rotateInArabic />
        </MinimalIconButton>

        <MinimalIconButton
          sx={{
            top: 16,
            right: 44,
            display: { xs: 'none', md: 'block' },
          }}
          onClick={handleNext}
        >
          <Icon name="ChevronRight" color="text.secondary" rotateInArabic />
        </MinimalIconButton>

        <MinimalIconButton
          sx={{
            top: { xs: 8, md: 16 },
            right: { xs: 0, md: 12 },
            zIndex: 2,
            backgroundColor: 'black !important',
            borderRadius: 0,
            pr: { xs: 2, md: 0.5 },
          }}
          onClick={onOpen}
        >
          <Icon name="Info" color="text.secondary" />
        </MinimalIconButton>
      </BorderStack>
      <CategoryFilterDialog
        subtitle={subtitle}
        categories={categories}
        filteredCategories={filteredCategories}
        onFilterCategory={handleFilterCategory}
        open={open}
        onClose={onClose}
      />
    </>
  );
};

export default CategorySelector;
